import './styles.scss';

import { ArrowLeftOutlined } from '@ant-design/icons';
import { ProductDetails, ProductItem } from '@components/product';
import { CreateButton, useDrawerForm, useSimpleList } from '@refinedev/antd';
import {
    BaseKey,
    CrudFilters,
    HttpError,
    IResourceComponentsProps,
    useGetIdentity,
    useNavigation,
    useOne,
    useTranslate,
} from '@refinedev/core';
import { Col, Form, List, Row } from 'antd';
import { useParams } from 'react-router-dom';

import { UserTypeEnum } from '../../enums/user-type.enum';
import { IProduct } from '../../interfaces/product';
import { IService } from '../../interfaces/service';
import { IUser } from '../../interfaces/user';

export const ProductIndex: React.FC<IResourceComponentsProps> = () => {
    const t = useTranslate();
    const { goBack, push } = useNavigation();

    const { id } = useParams();
    const { data, error } = useOne<IService>({
        resource: 'v1/services',
        id: id as BaseKey,
        errorNotification: false,
        queryOptions: {
            retry: 1,
        },
    });
    const { data: userIdentity } = useGetIdentity<IUser.IUserIdentity>();
    const isTenant = userIdentity?.userType === UserTypeEnum.TENANT;
    const isContact = userIdentity?.userType === UserTypeEnum.CLIENT;

    const { listProps, searchFormProps } = useSimpleList<IProduct, HttpError, { name: string }>({
        pagination: { pageSize: 12, current: 1 },
        resource: 'v1/products',
        initialFilter: [
            {
                field: 'serviceId',
                operator: 'eq',
                value: id,
            },
        ],
        onSearch: ({ name }) => {
            const productFilters: CrudFilters = [];

            productFilters.push({
                field: 'name',
                operator: 'eq',
                value: name ? name : undefined,
            });

            return productFilters;
        },
    });
    if (error) {
        const { statusCode } = error as { statusCode: number };
        if (statusCode === 404) {
            push('/404');
        }
    }

    const {
        drawerProps: createDrawerProps,
        formProps: createFormProps,
        saveButtonProps: createSaveButtonProps,
        show: createShow,
    } = useDrawerForm<IProduct>({
        action: 'create',
        resource: 'v1/products',
        successNotification: { message: 'Successfully created', type: 'success' },
        redirect: false,
        onMutationSuccess: () => {
            createFormProps.form.resetFields();
        },
    });

    const {
        drawerProps: editDrawerProps,
        formProps: editFormProps,
        saveButtonProps: editSaveButtonProps,
        show: editShow,
    } = useDrawerForm<IProduct>({
        action: 'edit',
        resource: 'v1/products',
        successNotification: { message: 'Successfully edited', type: 'success' },
        redirect: false,
        onMutationSuccess: () => {
            editFormProps.form.resetFields();
        },
    });

    return (
        <>
            <Form
                {...searchFormProps}
                onValuesChange={() => {
                    searchFormProps.form?.submit();
                }}
            >
                <Row gutter={[16, 16]} style={{ background: '#fff', padding: '16px 24px' }}>
                    <Col xs={24}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: '8px',
                                marginBottom: '16px',
                            }}
                        >
                            <label className="product-header-section">
                                <ArrowLeftOutlined onClick={goBack} />
                                {data?.data.name}
                            </label>
                            {data?.data?.tenantId === userIdentity?.tenantId && (
                                <CreateButton type="primary" onClick={() => createShow()}>
                                    {t('services.buttons.addProduct')}
                                </CreateButton>
                            )}
                        </div>
                        <List
                            grid={{
                                gutter: 20,
                                xs: 1,
                                sm: 1,
                                md: 2,
                                lg: 2,
                                xl: 3,
                                xxl: 4,
                            }}
                            style={{
                                height: '100%',
                                overflow: 'auto',
                                paddingRight: '20px',
                                paddingLeft: '20px',
                                marginLeft: '-20px',
                                marginRight: '-20px',
                            }}
                            {...listProps}
                            renderItem={(item: IProduct) => (
                                <ProductItem
                                    isTenant={isTenant}
                                    item={item}
                                    editShow={editShow}
                                    isContact={isContact}
                                    tenantId={data?.data?.tenantId}
                                />
                            )}
                        />
                    </Col>
                </Row>
            </Form>
            {id && (
                <>
                    <ProductDetails
                        drawerProps={createDrawerProps}
                        formProps={createFormProps}
                        saveButtonProps={createSaveButtonProps}
                        isEditMode={false}
                        serviceId={id}
                    />
                    <ProductDetails
                        isEditMode={true}
                        drawerProps={editDrawerProps}
                        formProps={editFormProps}
                        saveButtonProps={editSaveButtonProps}
                        serviceId={id}
                    />
                </>
            )}
        </>
    );
};
